import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "@/App";
import { usePolling } from "@/hooks/usePolling";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { SEO } from "@/components/SEO";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, ArrowsLeftRight, Check, Clock, CurrencyInr, User,
  Phone, Envelope, ArrowDown, ArrowUp, X,
  TrendUp, Handshake, CaretRight, Package
} from "@phosphor-icons/react";

export default function ReferralsPage() {
  const { token, user } = useAuth();
  const [myGiven, setMyGiven] = useState([]);
  const [myReceived, setMyReceived] = useState([]);
  const [categories, setCategories] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [convertValue, setConvertValue] = useState("");
  const [activeTab, setActiveTab] = useState("received");

  const [formData, setFormData] = useState({
    client_name: "", contact_phone: "", contact_email: "",
    business_category: "", description: "",
    referral_type: "open", direct_to_user_id: ""
  });

  const fetchData = useCallback(async () => {
    try {
      const [givenRes, receivedRes, catRes, membersRes] = await Promise.all([
        axios.get(`${API}/referrals/my-given`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/referrals/my-received`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/members`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setMyGiven(givenRes.data);
      setMyReceived(receivedRes.data);
      setCategories(catRes.data);
      setMembers(membersRes.data.filter(m => m.id !== user?.id));
    } catch (error) {
      console.error("Failed to fetch referrals:", error);
    } finally {
      setLoading(false);
    }
  }, [token, user?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);
  usePolling(fetchData, 10000);

  const handleSubmit = async () => {
    if (!formData.client_name || !formData.business_category || !formData.description) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!formData.contact_phone || formData.contact_phone.replace(/\D/g, "").length !== 10) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }
    try {
      await axios.post(`${API}/referrals`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Referral created successfully!");
      setDialogOpen(false);
      setFormData({ client_name: "", contact_phone: "", contact_email: "", business_category: "", description: "", referral_type: "open", direct_to_user_id: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create referral");
    }
  };

  const handleAccept = async (referralId) => {
    try {
      await axios.put(`${API}/referrals/${referralId}/accept`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Referral accepted!");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept referral");
    }
  };

  const handleUpdateStatus = async (referralId, status) => {
    try {
      await axios.put(`${API}/referrals/${referralId}/update`, { referral_status: status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Status updated!");
      fetchData();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const handleConvert = async () => {
    if (!convertValue || isNaN(parseFloat(convertValue))) {
      toast.error("Please enter a valid amount"); return;
    }
    try {
      await axios.put(`${API}/referrals/${selectedReferral.id}/update`, {
        referral_status: "converted", converted_value: parseFloat(convertValue)
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Referral marked as converted!");
      setConvertDialogOpen(false);
      setSelectedReferral(null);
      setConvertValue("");
      fetchData();
    } catch (error) {
      toast.error("Failed to convert referral");
    }
  };

  // Stats calculations
  const totalGiven = myGiven.length;
  const totalReceived = myReceived.length;
  const convertedGiven = myGiven.filter(r => r.status === "converted");
  const convertedReceived = myReceived.filter(r => r.status === "converted");
  const businessGiven = convertedGiven.reduce((sum, r) => sum + (r.converted_value || 0), 0);
  const businessEarned = convertedReceived.reduce((sum, r) => sum + (r.converted_value || 0), 0);

  const activeList = activeTab === "received" ? myReceived : myGiven;

  return (
    <>
      <SEO title="Referrals" description="Manage and track your business referrals — give, receive, and convert referrals within your BCH chapter." path="/referrals" noIndex />
      <div className="space-y-8" data-testid="referrals-page">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl sm:text-4xl text-primary tracking-tight">Referrals</h1>
            <p className="text-secondary text-sm mt-1">Track your given and received business referrals</p>
          </div>
          <div className="flex gap-2.5">
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-bch-teal hover:bg-bch-teal-dark rounded-xl" size="sm" data-testid="create-referral-button">
                  <Plus className="w-4 h-4 mr-1.5" /> New Referral
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-[var(--border-color)] max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-primary text-xl">Create Referral</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label className="text-secondary">Client Name *</Label>
                    <Input value={formData.client_name} onChange={(e) => setFormData(p => ({ ...p, client_name: e.target.value }))}
                      className="bg-surface border-[var(--border-color)] text-primary" placeholder="Client's name" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-secondary">Phone *</Label>
                      <Input value={formData.contact_phone}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, "").slice(0, 10);
                          setFormData(p => ({ ...p, contact_phone: val }));
                        }}
                        maxLength={10}
                        className="bg-surface border-[var(--border-color)] text-primary" placeholder="9876543210" />
                      {formData.contact_phone && formData.contact_phone.length !== 10 && (
                        <p className="text-red-400 text-xs">Must be exactly 10 digits</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label className="text-secondary">Email</Label>
                      <Input value={formData.contact_email} onChange={(e) => setFormData(p => ({ ...p, contact_email: e.target.value }))}
                        className="bg-surface border-[var(--border-color)] text-primary" placeholder="client@email.com" />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-secondary">Business Category *</Label>
                    <Select value={formData.business_category} onValueChange={(v) => setFormData(p => ({ ...p, business_category: v }))}>
                      <SelectTrigger className="bg-surface border-[var(--border-color)] text-primary">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-[var(--border-color)]">
                        {categories.map((cat) => (
                          <SelectItem key={cat.id} value={cat.name} className="text-primary">{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-secondary">Referral Type</Label>
                    <Select value={formData.referral_type} onValueChange={(v) => setFormData(p => ({ ...p, referral_type: v }))}>
                      <SelectTrigger className="bg-surface border-[var(--border-color)] text-primary">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-[var(--border-color)]">
                        <SelectItem value="open" className="text-primary">Open (Anyone can claim)</SelectItem>
                        <SelectItem value="direct" className="text-primary">Direct (Specific member)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.referral_type === "direct" && (
                    <div className="space-y-2">
                      <Label className="text-secondary">Send To Member</Label>
                      <MemberSearch members={members} value={formData.direct_to_user_id} onSelect={(id) => setFormData(p => ({ ...p, direct_to_user_id: id }))} />
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label className="text-secondary">Description *</Label>
                    <Textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))}
                      className="bg-surface border-[var(--border-color)] text-primary min-h-[100px]" placeholder="Describe the referral opportunity..." />
                  </div>
                </div>
                <DialogFooter>
                  <Button onClick={handleSubmit} className="bg-bch-gold hover:bg-bch-gold-dark text-black font-medium">Create Referral</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={ArrowDown} label="Received" value={totalReceived} color="teal" />
          <StatCard icon={ArrowUp} label="Given" value={totalGiven} color="gold" />
          <StatCard icon={TrendUp} label="Business Earned" value={`₹${(businessEarned / 100000).toFixed(1)}L`} color="green" isMono />
          <StatCard icon={Handshake} label="Business Given" value={`₹${(businessGiven / 100000).toFixed(1)}L`} color="violet" isMono />
        </div>

        {/* Tab Switcher */}
        <div className="flex items-center gap-1 bg-surface border border-[var(--border-color)] rounded-2xl p-1.5 w-fit">
          <button
            onClick={() => setActiveTab("received")}
            data-testid="tab-received"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
              activeTab === "received"
                ? "bg-bch-teal text-white shadow-lg shadow-bch-teal/20"
                : "text-secondary hover:text-primary hover:bg-surface"
            }`}
          >
            <ArrowDown className="w-4 h-4" weight="bold" />
            Received
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${activeTab === "received" ? "bg-white/20" : "bg-surface"}`}>
              {myReceived.length}
            </span>
          </button>
          <button
            onClick={() => setActiveTab("given")}
            data-testid="tab-given"
            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${
              activeTab === "given"
                ? "bg-bch-teal text-white shadow-lg shadow-bch-teal/20"
                : "text-secondary hover:text-primary hover:bg-surface"
            }`}
          >
            <ArrowUp className="w-4 h-4" weight="bold" />
            Given
            <span className={`ml-1 px-2 py-0.5 rounded-full text-xs ${activeTab === "given" ? "bg-white/20" : "bg-surface"}`}>
              {myGiven.length}
            </span>
          </button>
        </div>

        {/* Referral List */}
        <div className="space-y-4" data-testid="referral-list">
          {loading ? (
            <div className="flex justify-center py-20">
              <div className="w-8 h-8 border-2 border-bch-teal border-t-transparent rounded-full animate-spin" />
            </div>
          ) : activeList.length === 0 ? (
            <div className="text-center py-16 bg-card border border-[var(--border-color)] rounded-2xl">
              <div className="w-16 h-16 rounded-2xl bg-bch-teal/10 flex items-center justify-center mx-auto mb-4">
                <ArrowsLeftRight className="w-8 h-8 text-bch-teal/40" weight="duotone" />
              </div>
              <p className="text-secondary font-medium">No {activeTab} referrals yet</p>
              <p className="text-muted text-sm mt-1">
                {activeTab === "received" ? "Referrals sent to you will appear here" : "Create a new referral to get started"}
              </p>
            </div>
          ) : (
            activeList.map((referral, idx) => (
              <ReferralCard
                key={referral.id}
                referral={referral}
                currentUserId={user?.id}
                onAccept={handleAccept}
                onUpdateStatus={handleUpdateStatus}
                onConvert={(r) => { setSelectedReferral(r); setConvertDialogOpen(true); }}
                showAccept={activeTab === "received"}
                index={idx}
              />
            ))
          )}
        </div>

        {/* Convert Dialog */}
        <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
          <DialogContent className="bg-card border-[var(--border-color)]">
            <DialogHeader>
              <DialogTitle className="text-primary">Mark as Converted</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label className="text-secondary">Business Value (₹)</Label>
              <div className="relative mt-2">
                <CurrencyInr className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-bch-gold" weight="bold" />
                <Input type="number" value={convertValue} onChange={(e) => setConvertValue(e.target.value)}
                  className="pl-10 bg-surface border-[var(--border-color)] text-primary text-lg font-mono" placeholder="Enter amount" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={handleConvert} className="bg-green-500 hover:bg-green-600 text-white">
                <Check className="w-4 h-4 mr-2" /> Confirm Conversion
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}

/* ============== STAT CARD ============== */
const StatCard = ({ icon: Icon, label, value, color, isMono }) => {
  const colorMap = {
    teal: { bg: "bg-bch-teal/10", icon: "text-bch-teal", ring: "ring-bch-teal/20" },
    gold: { bg: "bg-bch-gold/10", icon: "text-bch-gold", ring: "ring-bch-gold/20" },
    green: { bg: "bg-emerald-500/10", icon: "text-emerald-500", ring: "ring-emerald-500/20" },
    violet: { bg: "bg-violet-500/10", icon: "text-violet-500", ring: "ring-violet-500/20" },
  };
  const c = colorMap[color] || colorMap.teal;
  return (
    <div className={`bg-card border border-[var(--border-color)] rounded-2xl p-5 hover:shadow-md transition-shadow duration-300`} data-testid={`stat-${label.toLowerCase().replace(" ", "-")}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${c.icon}`} weight="duotone" />
        </div>
        <span className="text-muted text-xs font-semibold uppercase tracking-wider">{label}</span>
      </div>
      <p className={`text-primary text-2xl font-bold ${isMono ? "font-mono" : ""}`}>{value}</p>
    </div>
  );
};

/* ============== REFERRAL CARD ============== */
const STATUS_CONFIG = {
  new: { bg: "bg-sky-500/15", text: "text-sky-400", dot: "bg-sky-400", label: "New" },
  accepted: { bg: "bg-bch-teal/15", text: "text-bch-teal", dot: "bg-bch-teal", label: "Accepted" },
  in_progress: { bg: "bg-amber-500/15", text: "text-amber-400", dot: "bg-amber-400", label: "In Progress" },
  converted: { bg: "bg-emerald-500/15", text: "text-emerald-400", dot: "bg-emerald-400", label: "Converted" },
  closed: { bg: "bg-surface", text: "text-muted", dot: "bg-muted", label: "Closed" },
};

const ReferralCard = ({ referral, currentUserId, onAccept, onUpdateStatus, onConvert, showAccept, index }) => {
  const sc = STATUS_CONFIG[referral.status] || STATUS_CONFIG.new;

  return (
    <div
      className="bg-card border border-[var(--border-color)] rounded-2xl overflow-hidden transition-all duration-300 hover:border-bch-teal/30 hover:shadow-lg hover:shadow-bch-teal/5"
      data-testid={`referral-card-${referral.id}`}
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Top colored status strip */}
      <div className={`h-1 ${sc.dot}`} />

      <div className="p-6">
        <div className="flex items-start justify-between gap-4">
          {/* Left Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-primary text-lg font-bold truncate" data-testid={`referral-name-${referral.id}`}>
                {referral.client_name}
              </h3>
              <span className={`shrink-0 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${sc.bg} ${sc.text}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                {sc.label}
              </span>
              {referral.referral_type === "direct" && (
                <span className="shrink-0 px-2.5 py-1 bg-violet-500/15 text-violet-400 text-[11px] font-bold uppercase tracking-wider rounded-full">Direct</span>
              )}
            </div>

            <div className="flex items-center gap-2 mt-2">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-bch-gold/10 text-bch-gold rounded-lg text-xs font-semibold">
                <Package className="w-3.5 h-3.5" weight="duotone" />
                {referral.business_category}
              </span>
            </div>

            <p className="text-secondary text-sm mt-3 leading-relaxed line-clamp-2">{referral.description}</p>

            {/* Contact info */}
            {(referral.contact_phone || referral.contact_email) && (
              <div className="flex flex-wrap gap-4 mt-3">
                {referral.contact_phone && (
                  <a href={`tel:${referral.contact_phone}`} className="flex items-center gap-1.5 text-muted text-xs hover:text-bch-teal transition-colors">
                    <Phone className="w-3.5 h-3.5" weight="duotone" /> {referral.contact_phone}
                  </a>
                )}
                {referral.contact_email && (
                  <a href={`mailto:${referral.contact_email}`} className="flex items-center gap-1.5 text-muted text-xs hover:text-bch-teal transition-colors">
                    <Envelope className="w-3.5 h-3.5" weight="duotone" /> {referral.contact_email}
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Right — Converted Value */}
          {referral.converted_value > 0 && (
            <div className="shrink-0 text-right bg-emerald-500/10 rounded-xl px-4 py-3">
              <p className="text-emerald-400/60 text-[10px] uppercase tracking-wider font-bold">Value</p>
              <p className="text-emerald-400 font-mono text-xl font-bold mt-0.5">₹{referral.converted_value.toLocaleString("en-IN")}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="mt-5 pt-4 border-t border-[var(--border-color)] flex items-center justify-between">
          <div className="flex items-center gap-4 text-xs text-muted">
            <span className="flex items-center gap-1.5">
              <User className="w-3.5 h-3.5" weight="duotone" />
              From: <span className="text-secondary font-medium">{referral.given_by_name}</span>
            </span>
            {referral.accepted_by_name && (
              <>
                <CaretRight className="w-3 h-3 text-muted/40" />
                <span>To: <span className="text-secondary font-medium">{referral.accepted_by_name}</span></span>
              </>
            )}
          </div>

          <div className="flex items-center gap-2">
            {showAccept && referral.status === "new" && referral.given_by_id !== currentUserId && (
              <Button size="sm" onClick={() => onAccept(referral.id)}
                className="bg-bch-teal hover:bg-bch-teal-dark rounded-xl text-xs h-8 px-4" data-testid={`accept-${referral.id}`}>
                <Check className="w-3.5 h-3.5 mr-1" /> Accept
              </Button>
            )}
            {referral.accepted_by_id === currentUserId && referral.status === "accepted" && (
              <Button size="sm" onClick={() => onUpdateStatus(referral.id, "in_progress")}
                className="bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 rounded-xl text-xs h-8 px-4" data-testid={`progress-${referral.id}`}>
                <Clock className="w-3.5 h-3.5 mr-1" /> In Progress
              </Button>
            )}
            {referral.accepted_by_id === currentUserId && referral.status === "in_progress" && (
              <Button size="sm" onClick={() => onConvert(referral)}
                className="bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 rounded-xl text-xs h-8 px-4" data-testid={`convert-${referral.id}`}>
                <Check className="w-3.5 h-3.5 mr-1" /> Convert
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============== MEMBER SEARCH AUTOCOMPLETE ============== */
const MemberSearch = ({ members, value, onSelect }) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const selected = members.find(m => m.id === value);
  const filtered = query.trim()
    ? members.filter(m =>
        m.full_name?.toLowerCase().includes(query.toLowerCase()) ||
        m.business_name?.toLowerCase().includes(query.toLowerCase()) ||
        m.chapter_name?.toLowerCase().includes(query.toLowerCase())
      )
    : members;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={ref} data-testid="member-search">
      <Input
        value={selected ? `${selected.full_name} - ${selected.business_name || ""}` : query}
        onChange={(e) => {
          setQuery(e.target.value);
          if (value) onSelect("");
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        placeholder="Type name to search..."
        className="bg-surface border-[var(--border-color)] text-primary"
        data-testid="member-search-input"
      />
      {selected && (
        <button
          onClick={() => { onSelect(""); setQuery(""); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted hover:text-primary"
          data-testid="member-search-clear"
        >
          <X className="w-4 h-4" weight="bold" />
        </button>
      )}
      {open && !selected && filtered.length > 0 && (
        <div className="absolute z-50 mt-1 w-full bg-card border border-[var(--border-color)] rounded-xl shadow-lg max-h-48 overflow-y-auto" data-testid="member-search-dropdown">
          {filtered.slice(0, 20).map(m => (
            <button
              key={m.id}
              onClick={() => { onSelect(m.id); setQuery(""); setOpen(false); }}
              className="w-full text-left px-4 py-2.5 hover:bg-surface transition-colors border-b border-[var(--border-color)] last:border-b-0"
              data-testid={`member-option-${m.id}`}
            >
              <p className="text-primary text-sm font-medium">{m.full_name}</p>
              <p className="text-muted text-xs">{m.business_name || ""} · {m.chapter_name || "No chapter"}</p>
            </button>
          ))}
        </div>
      )}
      {open && !selected && query && filtered.length === 0 && (
        <div className="absolute z-50 mt-1 w-full bg-card border border-[var(--border-color)] rounded-xl shadow-lg p-4 text-center">
          <p className="text-muted text-sm">No members found</p>
        </div>
      )}
    </div>
  );
};